// AF-3.19 P1 — Creator chain: Production / Video. Reuses the existing
// AF-3.10 creatorCommandAPI.productionQueue() route (already wrapped in
// intelligenceApi.js since AF-3.16) rather than adding a new client for
// the raw AF-3.9 video routes — this keeps the same READY/APPROVED
// distinction Creator Command Center already enforces, without a second
// production-status computation.

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { StatusBadge, ApprovalBadge } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { EvidenceDrawer } from '@/components/brand/EvidenceDrawer';
import { useBrandContext } from '@/hooks/useBrandContext';
import { creatorCommandAPI } from '@/lib/intelligenceApi';
import { CaretRight } from '@phosphor-icons/react';

// AF-3.20 Phase 10 — explicit READY FOR APPROVAL / APPROVED — NOT
// PUBLISHED copy, derived only from the backend's own production_status/
// approval_status fields, never a client-side guess.
function readinessCopy(c) {
  if (c.approval_status === 'APPROVED') return 'APPROVED — NOT PUBLISHED';
  if (c.production_status === 'READY') return 'READY FOR APPROVAL';
  return null;
}

// AF-3.19 Phase 7 — minimal Strategy -> Ideas -> Review -> Decisions ->
// Production -> Distribution stepper, shared shape with the other Creator
// chain pages, so this page's position in the sequence is obvious at a
// glance.
const CHAIN_STAGES = ['Strategy', 'Ideas', 'Review', 'Decisions', 'Production', 'Distribution'];

function ChainProgress({ current }) {
  return (
    <div data-testid="creator-chain-progress" className="flex items-center gap-1.5 flex-wrap mb-3">
      {CHAIN_STAGES.map((stage, i) => (
        <span key={stage} className="flex items-center gap-1.5">
          <span className={`text-[10px] font-mono uppercase ${stage === current ? 'text-[var(--accent)] font-medium' : 'text-[var(--text-tertiary)]'}`}>{stage}</span>
          {i < CHAIN_STAGES.length - 1 && <CaretRight size={10} className="text-[var(--text-tertiary)]" />}
        </span>
      ))}
    </div>
  );
}

export default function CreatorProductionPage() {
  const ctx = useBrandContext();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [evidenceFor, setEvidenceFor] = useState(null);

  const load = useCallback(() => {
    if (!ctx.workspaceId) return;
    setLoading(true); setError(null);
    creatorCommandAPI.productionQueue(ctx.workspaceId)
      .then((r) => setQueue(r.data?.commands || r.data || []))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.workspaceId]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div data-testid="creator-production-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Production / Video</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">READY (generated) ≠ APPROVED (reviewed) ≠ PUBLISHED (live) — status only, no execution here.</p>
        </div>

        <ChainProgress current="Production" />

        <div className="mb-4">
          <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">Workspace</p>
          <p className="text-[12px] text-[var(--text-primary)]">{ctx.clients.find((c) => c.id === ctx.workspaceId)?.name || ctx.workspaceId || 'No workspace selected'}</p>
        </div>

        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && queue.length === 0 && (
          <EmptyState title="Production queue is empty" description="No command is currently in production for this workspace." />
        )}

        {!loading && !error && queue.length > 0 && (
          <div className="space-y-2" data-testid="creator-production-list">
            {queue.map((c) => (
              <div key={c.id} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)] flex items-center justify-between flex-wrap gap-2" data-testid="creator-production-row">
                <div>
                  <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">Command {c.id?.slice(0, 8)}…</p>
                  <p className="text-[12px] text-[var(--text-primary)]">{c.next_action || c.current_stage?.replace(/_/g, ' ') || 'In production'}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    <StatusBadge value={c.production_status} />
                    {c.approval_required && <ApprovalBadge value={c.approval_status || 'PENDING'} />}
                  </div>
                  {readinessCopy(c) && <p className="text-[10px] text-[var(--text-secondary)]" data-testid="production-readiness-copy">{readinessCopy(c)}</p>}
                  <button data-testid="creator-production-evidence-trigger" className="text-[11px] text-[var(--accent)]" onClick={() => setEvidenceFor(c)}>
                    Why this? →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {evidenceFor && (
          <EvidenceDrawer
            open={!!evidenceFor}
            onOpenChange={(open) => !open && setEvidenceFor(null)}
            title="Why this is in production"
            evidence={[]}
            provenance={{
              opportunity_id: evidenceFor.opportunity_id ? [evidenceFor.opportunity_id] : [],
              primary_production_asset_id: evidenceFor.primary_production_asset_id ? [evidenceFor.primary_production_asset_id] : [],
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
